// src/pages/markets/board/card/body/SingleMarketBlock.tsx
//
// One binary market, no ladder to draw. Both outcomes are named above the bar
// and priced below it, so the bar reads as a split between two sides rather
// than as one side's progress meter. The capital line that used to sit at the
// bottom of this frame is gone: the conviction row underneath already prints it.
//
// With no series the block is just the head, and the card shrinks to match —
// an empty chart frame is worse than no chart frame.

import { numSx } from '../../../../../components/common/page/numberStyles';
import { useMemo } from 'react';
import { Box, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import type {
    BoardScore,
    MarketSeries,
    ScreenerMarket,
} from '../../api/boardTypes';
import { BLOCK_GAP, SINGLE_HEAD_HEIGHT } from '../cardLayout';
import { CardTrendChart } from '../CardTrendChart';
import { EntryMarkerLayer } from '../EntryMarkerLayer';
import { seriesSpanMs } from '../trendGeometry';
import { useEntryMarkers } from '../../api/useEntryMarkers';
import { markerChipText } from '../../utils/entryMarkers';
import { EM_DASH, formatProb, priceMeta } from '../cardFormat';
import { sideLabels } from '../../utils/boardOutcomeLabels';
import { sideColor } from '../../utils/sideColor';
import { OUTCOME_MAXLEN_BADGE } from '../../../utils/outcomeLabels';

export interface SingleMarketBlockProps {
    market: ScreenerMarket | null;
    score: BoardScore | undefined;
    outcomes?: string[] | null;
    height: number;
    series?: MarketSeries | null;
    /** The column's series window, so a 24h line is not labelled 7d. */
    seriesWindow?: string;
    group?: string | null;
    eventId?: string;
    now?: number;
}

const labelSx = {
    fontSize: '12px',
    lineHeight: '16px',
    color: 'text.secondary',
    minWidth: 0,
    overflow: 'hidden',
    whiteSpace: 'nowrap',
    textOverflow: 'ellipsis',
} as const;

const priceSx = {
    fontSize: '16px',
    lineHeight: '20px',
    fontWeight: 700,
    ...numSx,
} as const;

export function SingleMarketBlock({
    market,
    score,
    outcomes,
    height,
    series,
    seriesWindow,
    group,
    eventId,
    now,
}: SingleMarketBlockProps) {
    const theme = useTheme();
    const labels = sideLabels(market, OUTCOME_MAXLEN_BADGE, { outcomes });
    const nowMs = now ?? Date.now();
    const price = priceMeta(market, nowMs);
    const prob = price.value;
    const pct = prob === null ? 0 : Math.max(0, Math.min(1, prob));
    // sideColor owns this branch; re-deriving it here is how the two drift.
    const yesColor = sideColor(labels, 'YES', theme);
    const noColor = sideColor(labels, 'NO', theme);

    const span = useMemo(() => seriesSpanMs(series?.points), [series]);
    const tracked = (score?.n_traders_yes ?? 0) + (score?.n_traders_no ?? 0);
    // No window, no group, nobody tracked — nothing to ask the server for.
    const wantsMarkers = Boolean(span && group && market && tracked > 0);
    const entries = useEntryMarkers(
        market?.market_id ?? null,
        group ?? null,
        wantsMarkers,
        {
            nowMs,
            windowStartMs: span?.firstMs ?? null,
        }
    );
    const chipText = markerChipText(entries);
    const extraValues = useMemo(
        () => entries.markers.map(m => m.yesPrice),
        [entries.markers]
    );

    return (
        <Box
            style={{ height }}
            sx={{
                display: 'flex',
                flexDirection: 'column',
                boxSizing: 'border-box',
            }}
        >
            <Box
                style={{ height: SINGLE_HEAD_HEIGHT }}
                sx={{
                    flexShrink: 0,
                    display: 'flex',
                    flexDirection: 'column',
                    boxSizing: 'border-box',
                }}
            >
                <Box
                    sx={{
                        height: '16px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        gap: 1,
                    }}
                >
                    <Typography title={labels.yes.title} sx={labelSx}>
                        {labels.yes.text}
                    </Typography>
                    <Typography
                        title={labels.no.title}
                        sx={{ ...labelSx, textAlign: 'right' }}
                    >
                        {labels.no.text}
                    </Typography>
                </Box>

                <Box
                    sx={{
                        mt: '5px',
                        height: '8px',
                        borderRadius: '4px',
                        overflow: 'hidden',
                        display: 'flex',
                        bgcolor: noColor,
                    }}
                >
                    <Box sx={{ width: `${pct * 100}%`, bgcolor: yesColor }} />
                </Box>

                <Box
                    sx={{
                        mt: '4px',
                        height: '20px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        gap: 1,
                    }}
                >
                    <Typography title={price.title} sx={{ ...priceSx, color: yesColor }}>
                        {prob === null ? EM_DASH : formatProb(prob)}
                    </Typography>
                    <Typography title={price.title} sx={{ ...priceSx, color: noColor }}>
                        {prob === null ? EM_DASH : formatProb(1 - prob)}
                    </Typography>
                </Box>
            </Box>

            {/* span is null when the points cannot make a line — same as no series. */}
            {series && span ? (
                <Box sx={{ mt: `${BLOCK_GAP}px`, flex: 1, minHeight: 0 }}>
                    <CardTrendChart
                        points={series.points}
                        referenceValue={null}
                        referenceColor={yesColor}
                        windowLabel={seriesWindow ?? '7d'}
                        showLast={false}
                        extraValues={extraValues}
                        overlay={geo => (
                            <EntryMarkerLayer
                                geo={geo}
                                markers={entries.markers}
                                chipText={chipText}
                                labels={labels}
                                marketId={market?.market_id ?? ''}
                                eventId={eventId ?? ''}
                                now={nowMs}
                            />
                        )}
                    />
                </Box>
            ) : null}
        </Box>
    );
}